import React from 'react';
import { useTranslation } from 'react-i18next';
import { calculatePieceMetrics, type ProductLogisticsInput } from '../productLogistics';
import { PieceDrawing } from './ProductCardDrawings';
import {
  CardNumberField,
  MAX_DIMENSION_MM,
  MAX_WEIGHT_KG,
  nonNegativeNumberRule,
} from './cardFields';
import { formatReadout } from './cardFormat';
import CardSectionHeader from './CardSectionHeader';

interface PieceSectionProps {
  values: ProductLogisticsInput;
  /** The product already uses "height" as a plane dimension (e.g. windows). */
  showHeight: boolean;
  disabled: boolean;
}

const Tile: React.FC<{ label: string; value: string; ok: boolean }> = ({ label, value, ok }) => (
  <div className={ok ? 'product-card-tile' : 'product-card-tile is-empty'}>
    <div className="product-card-tile__label">{label}</div>
    <div className="product-card-tile__value">{value}</div>
  </div>
);

/** "Wymiary i waga sztuki": piece dimensions, piece weight and conversion tiles. */
const PieceSection: React.FC<PieceSectionProps> = ({ values, showHeight, disabled }) => {
  const { t } = useTranslation(['products']);
  const piece = calculatePieceMetrics(values);
  const hasWeight = Boolean(piece.weightKg);
  return (
    <section className="product-card-section">
      <CardSectionHeader
        title={t('products:card.piece.title')}
        subtitle={t('products:card.piece.subtitle')}
        hint={t('products:card.piece.hint')}
      />
      <div className="product-card-dims">
        <PieceDrawing
          length={values.length}
          width={values.width}
          thickness={values.thickness}
          label={t('products:card.piece.drawingLabel')}
          lengthLabel={t('products:card.piece.length').toLocaleLowerCase('pl')}
          widthLabel={t('products:card.piece.width').toLocaleLowerCase('pl')}
        />
        <div className="product-card-grid">
          <CardNumberField name="length" label={t('products:card.piece.length')} suffix="mm" disabled={disabled}
            rules={[nonNegativeNumberRule(t, MAX_DIMENSION_MM)]} />
          <CardNumberField name="width" label={t('products:card.piece.width')} suffix="mm" disabled={disabled}
            rules={[nonNegativeNumberRule(t, MAX_DIMENSION_MM)]} />
          <CardNumberField name="thickness" label={t('products:card.piece.thickness')} suffix="mm" disabled={disabled}
            rules={[nonNegativeNumberRule(t, MAX_DIMENSION_MM)]} />
          {showHeight && (
            <CardNumberField name="height" label={t('products:card.piece.height')} suffix="mm" disabled={disabled}
              extra={t('products:card.piece.heightHint')} rules={[nonNegativeNumberRule(t, MAX_DIMENSION_MM)]} />
          )}
          <CardNumberField name="grossWeight" label={t('products:card.piece.weight')} suffix="kg" disabled={disabled}
            rules={[nonNegativeNumberRule(t, MAX_WEIGHT_KG)]} />
        </div>
      </div>
      <div className="product-card-tiles">
        <Tile label={t('products:card.piece.tilePieceIs')}
          value={piece.areaM2 ? `${formatReadout(piece.areaM2, 3)} m²` : t('products:card.piece.enterLengthWidth')}
          ok={Boolean(piece.areaM2)} />
        <Tile label={t('products:card.piece.tilePieceIs')}
          value={piece.volumeM3 ? `${formatReadout(piece.volumeM3, 4)} m³` : t('products:card.piece.enterThickness')}
          ok={Boolean(piece.volumeM3)} />
        <Tile label={t('products:card.piece.tileM2Is')}
          value={piece.piecesPerM2 ? `${formatReadout(piece.piecesPerM2, 3)} szt.` : '—'}
          ok={Boolean(piece.piecesPerM2)} />
        <Tile label={t('products:card.piece.tileM2Weighs')}
          value={piece.kgPerM2 ? `${formatReadout(piece.kgPerM2, 2)} kg` : (hasWeight ? '—' : t('products:card.piece.enterWeight'))}
          ok={Boolean(piece.kgPerM2)} />
      </div>
    </section>
  );
};

export default PieceSection;
